import { Row } from '../layout/Stack.js'
import { Tabs, type TabItem } from '../ui/Tabs.js'

export interface FilterBarProps {
  /** The status filters across the top. Omit where a table has none. */
  tabs?: TabItem[]
  tab?: string
  onTabChange?: (value: string) => void
  /**
   * A further filter, on the right — a date range, a city, a kind.
   *
   * It belongs on the same row as the tabs and not in a card of its own above
   * the table. Bookings and Payments each had their tabs here and their date
   * range in a separate bordered box underneath, so two filters for one table
   * sat in two places with different padding and neither lined up.
   */
  filters?: React.ReactNode
}

/**
 * The row above a table: what narrows it.
 *
 * **The search is not here any more.** The kit and the working prototype both
 * put it in `.fawn-desk__head`, beside the page's one button, and having it
 * down here split "what this page is" from "how you narrow it" across two rows
 * on every list screen. `PageHeader` takes it now.
 *
 * What is left is the status tabs and anything else that narrows the same
 * table, on one line — which is a thin component, and rightly so: six screens
 * each arranging the same two parts slightly differently is the drift the
 * component layer exists to stop.
 */
export function FilterBar({ tabs, tab, onTabChange, filters }: FilterBarProps) {
  return (
    /**
     * Two rows on a phone, one from `sm` up.
     *
     * The tabs and the dropdowns shared a line at every width, and a `shrink-0`
     * dropdown wins that argument: at 390px *"All · Active · Suspended ·
     * Awaiting their code · Closed"* was cut off after three and a half, with
     * no sign there was more. The tabs scroll sideways — they always have —
     * but a strip that is *also* being squeezed from the right scrolls in a
     * space too small to scroll usefully.
     *
     * Given their own line they get the full width, and the dropdowns get a
     * line where they can be read.
     */
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      {tabs && tab !== undefined && onTabChange ? (
        <Tabs
          items={tabs}
          value={tab}
          onValueChange={onTabChange}
          className="w-full min-w-0 sm:flex-1"
        />
      ) : (
        <span className="hidden flex-1 sm:block" />
      )}
      {filters && (
        /* `flex-wrap` because two dropdowns and a date range do not fit 390px
           either, and a second line is better than a third scroller. */
        <Row gap={2} className="w-full flex-wrap sm:w-auto sm:flex-nowrap sm:shrink-0">
          {filters}
        </Row>
      )}
    </div>
  )
}
